// Analytics layout route (REC-88).
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Play, RefreshCw } from 'lucide-react';
import { DateRangePicker } from '@/features/analytics/DateRangePicker';
import { RollupStatus } from '@/features/analytics/RollupStatus';
import { AnalyticsSubNav } from '@/features/analytics/AnalyticsSubNav';
import { useInvalidateAnalytics, useTriggerRollup } from '@/features/analytics/api';
import { buildDateRange, analyticsSearchSchema } from '@/features/analytics/dateRange';
import type { DateRange } from '@/features/analytics/types';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/analytics')({
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = analyticsSearchSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
  },
  component: AnalyticsLayout,
});

function AnalyticsLayout() {
  const rawSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const invalidateAll = useInvalidateAnalytics();
  const [refreshing, setRefreshing] = useState(false);
  const triggerRollup = useTriggerRollup();
  const [rollupMessage, setRollupMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const range: DateRange = buildDateRange({
    preset: rawSearch.preset,
    from: rawSearch.from,
    to: rawSearch.to,
  });

  function handleRangeChange(next: DateRange) {
    void navigate({
      search: { preset: next.preset, from: next.from, to: next.to },
    });
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await invalidateAll();
    } finally {
      setRefreshing(false);
    }
  }

  async function handleRunRollup() {
    setRollupMessage(null);
    try {
      const result = await triggerRollup.mutateAsync({ from: range.from, to: range.to });
      const { summary } = result;
      if (summary.failure === 0) {
        setRollupMessage({
          tone: 'success',
          text: `Rollup complete — ${summary.success} day${summary.success === 1 ? '' : 's'} processed.`,
        });
      } else {
        setRollupMessage({
          tone: 'error',
          text: `Rollup finished with errors — ${summary.success} succeeded, ${summary.failure} failed.`,
        });
      }
    } catch (err) {
      setRollupMessage({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Rollup request failed.',
      });
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Page header */}
      <header className="mb-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl leading-tight">Analytics</h1>
            <p className="text-ink-soft text-sm mt-1">
              Usage, engagement, and business metrics across Honeycomb.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <button
              type="button"
              onClick={() => void handleRunRollup()}
              disabled={triggerRollup.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-ink-soft hover:text-ink hover:bg-honey-tint transition-colors disabled:opacity-50"
              title={`Re-run the daily rollup for ${range.from} → ${range.to}`}
            >
              <Play size={14} className={triggerRollup.isPending ? 'animate-pulse' : ''} />
              {triggerRollup.isPending ? 'Running rollup…' : 'Run rollup'}
            </button>
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-ink-soft hover:text-ink hover:bg-honey-tint transition-colors disabled:opacity-50"
              title="Refresh analytics data"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
        {rollupMessage && (
          <div
            role="status"
            className={`text-sm rounded-lg px-3 py-2 border ${
              rollupMessage.tone === 'success'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                : 'border-rose-300 bg-rose-50 text-rose-900'
            }`}
          >
            {rollupMessage.text}
          </div>
        )}

        {/* Date-range picker + rollup status */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <DateRangePicker value={range} onChange={handleRangeChange} />
          <RollupStatus />
        </div>

        {/* Sub-nav */}
        <AnalyticsSubNav range={range} />
      </header>

      {/* Child view */}
      <Outlet />
    </div>
  );
}
